import type {
  CreateCustomerVisitSessionRequest,
  CustomerGroupBuyRequestReadModel,
  CustomerSelfServiceBootstrapReadModel,
  CustomerSelfServiceReservationReadModel,
  CustomerSelfServiceWorkspace,
  CustomerVisitSessionReadModel,
  CustomerVisitSessionRevokeRequest,
  SelfServiceCustomerReservationCreateRequest,
  SelfServiceGroupBuyRequestCreateRequest,
} from "@billiard/contracts";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  CustomerSelfServiceApiError,
  cancelSelfServiceReservation,
  createCustomerVisitSession,
  createSelfServiceGroupBuyRequest,
  createSelfServiceReservation,
  getCustomerGroupBuyRequestByRequest,
  getCustomerSelfServiceBootstrap,
  getCustomerSelfServiceMutationByRequest,
  getCustomerSelfServiceWorkspace,
  revokeCustomerVisitSession,
} from "./customerSelfServiceApi";
import {
  beginCustomerGroupBuyRequestDispatch,
  beginCustomerSelfServiceMutationDispatch,
  beginCustomerVisitSessionCreationDispatch,
  beginCustomerVisitSessionRevokeDispatch,
  type CustomerGroupBuyRequestRecovery,
  type CustomerSelfServiceIdentity,
  type CustomerSelfServiceMutationCommand,
  type CustomerSelfServiceMutationRecovery,
  type CustomerSelfServiceRecoveryView,
  type CustomerVisitSessionCreationRecovery,
  type CustomerVisitSessionRevokeRecovery,
  claimCustomerGroupBuyRequestRecovery,
  claimCustomerSelfServiceMutationRecovery,
  claimCustomerVisitSessionCreationRecovery,
  claimCustomerVisitSessionRevokeRecovery,
  clearCustomerGroupBuyRequestAfterNotFound,
  clearCustomerGroupBuyRequestRecovery,
  clearCustomerSelfServiceMutationAfterNotFound,
  clearCustomerSelfServiceMutationRecovery,
  clearCustomerSelfServiceSession,
  clearCustomerVisitSessionCreationRecovery,
  clearCustomerVisitSessionRevokeAfterConfirmedInvalid,
  clearCustomerVisitSessionRevokeAfterDefiniteNoEffect,
  completeCustomerGroupBuyRequestRecovery,
  completeCustomerSelfServiceMutationRecovery,
  completeCustomerVisitSessionCreation,
  completeCustomerVisitSessionRevoke,
  customerGroupBuyRequestRecoveryCanQuery,
  customerGroupBuyRequestResultMatchesRecovery,
  customerSelfServiceMutationCanRetryAfterNotFound,
  customerSelfServiceMutationOutcomeIsUnknown,
  customerSelfServiceMutationRecoveryCanQuery,
  customerSelfServiceMutationResultMatchesRecovery,
  customerVisitSessionIsExactlyInvalid,
  customerVisitSessionRevokeResultMatchesRecovery,
  finishCustomerGroupBuyRequestDispatch,
  finishCustomerSelfServiceMutationDispatch,
  finishCustomerVisitSessionCreationDispatch,
  finishCustomerVisitSessionRevokeDispatch,
  markCustomerGroupBuyRequestOutcomeUnknown,
  markCustomerSelfServiceMutationOutcomeUnknown,
  markCustomerVisitSessionCreationOutcomeUnknown,
  markCustomerVisitSessionRevokeOutcomeUnknown,
  quarantineCustomerGroupBuyRequestRecovery,
  quarantineCustomerSelfServiceMutationRecovery,
  quarantineCustomerVisitSessionRevokeRecovery,
  readCustomerGroupBuyRequestRecovery,
  readCustomerSelfServiceMutationRecovery,
  readCustomerSelfServiceSession,
  readCustomerVisitSessionCreationRecovery,
  readCustomerVisitSessionRevokeRecovery,
} from "./customerSelfServiceRecovery";
import "./CustomerSelfServicePortal.css";

const stableIdPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;
const storeLocalDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u;
const durationChoices = Array.from({ length: 24 }, (_, index) => (index + 1) * 30);

const stateLabels: Record<CustomerSelfServiceReservationReadModel["state"], string> = {
  PENDING_CONFIRMATION: "待门店确认",
  CONFIRMED: "门店已确认",
  WAITING: "到店排队中",
  CALLED: "已叫号",
  SEATED: "已入座开台",
  CANCELLED: "已取消",
  NO_SHOW: "未到店",
  EXPIRED: "已过期",
};

const groupBuyProviderLabels = { MEITUAN: "美团", DOUYIN: "抖音" } as const;

const groupBuyRequestStateLabels: Record<CustomerGroupBuyRequestReadModel["status"], string> = {
  PENDING: "等待收银员办理",
  PROCESSING: "收银员正在办理",
  COMPLETED: "已核销并开台",
  REJECTED: "未完成，请联系工作人员",
  NEEDS_REVIEW: "本条记录需要复核",
};

function groupBuyRequestStatusText(request: CustomerGroupBuyRequestReadModel): string {
  switch (request.status) {
    case "PENDING":
      return "门店已收到提醒，但尚未核销券码，也没有开台。请向收银员出示平台中的完整券码。";
    case "PROCESSING":
      return "收银员正在核对原请求。请勿再次提交，继续向工作人员出示同一张完整券码。";
    case "COMPLETED":
      return `收银员已完成核销并开台${request.linkedRedemptionNumber ? `，核销编号 ${request.linkedRedemptionNumber}` : ""}。`;
    case "REJECTED":
      return "本次核销未完成。请让工作人员核对券码、套餐、球台或平台返回原因；其他业务仍可使用。";
    case "NEEDS_REVIEW":
      return "系统仍在线，但只有这条团购记录需要工作人员继续复核；这不代表整套系统离线。";
  }
}

type Feedback = { kind: "SUCCESS" | "ERROR" | "INFO"; text: string };

function secureBytes(length: number): Uint8Array {
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error("SECURE_RANDOM_UNAVAILABLE");
  }
  return globalThis.crypto.getRandomValues(new Uint8Array(length));
}

function secureToken(): string {
  const binary = Array.from(secureBytes(32), (value) => String.fromCharCode(value)).join("");
  return globalThis.btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function nextIdempotencyKey(prefix: string): string {
  const random = Array.from(secureBytes(16), (value) => value.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${prefix}:${random}`;
}

function storeDateTimeParts(value: string | Date, timeZone: string): Record<string, string> {
  return Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(typeof value === "string" ? new Date(value) : value)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
}

function storeLocalDateTimeValue(value: string | Date, timeZone: string): string {
  const parts = storeDateTimeParts(value, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function defaultStartValue(referenceAt: string, timeZone: string): string {
  const oneHourLater = new Date(Date.parse(referenceAt) + 60 * 60_000);
  const local = storeLocalDateTimeValue(oneHourLater, timeZone);
  const match = storeLocalDateTimePattern.exec(local);
  if (!match) return "";
  const wallEpoch = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );
  return new Date(Math.ceil(wallEpoch / (30 * 60_000)) * 30 * 60_000).toISOString().slice(0, 16);
}

export function storeLocalDateTimeToIso(
  value: string,
  timeZone: string,
): { status: "VALID"; value: string } | { status: "INVALID" | "AMBIGUOUS" } {
  const match = storeLocalDateTimePattern.exec(value);
  if (!match) return { status: "INVALID" };
  const wallEpoch = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );
  if (new Date(wallEpoch).toISOString().slice(0, 16) !== value) return { status: "INVALID" };
  const matches: string[] = [];
  for (let offsetMinutes = -14 * 60; offsetMinutes <= 14 * 60; offsetMinutes += 15) {
    const candidate = new Date(wallEpoch - offsetMinutes * 60_000);
    if (storeLocalDateTimeValue(candidate, timeZone) === value)
      matches.push(candidate.toISOString());
  }
  if (matches.length === 0) return { status: "INVALID" };
  if (matches.length > 1) return { status: "AMBIGUOUS" };
  return { status: "VALID", value: matches[0] as string };
}

function displayDateTime(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function errorText(error: unknown, fallback: string): string {
  if (error instanceof CustomerSelfServiceApiError) {
    if (error.status === 401) return "本设备的访客会话已失效，请重新登记后再操作。";
    if (error.status === 403)
      return "当前访客会话不能完成这项操作，请刷新核对或向门店工作人员求助。";
    if (/^[\s\S]*[\u3400-\u9fff][\s\S]*$/u.test(error.message)) return error.message;
  }
  return fallback;
}

function durationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder} 分钟`;
  return remainder === 0 ? `${hours} 小时` : `${hours} 小时 ${remainder} 分钟`;
}

function mutationLabel(command: CustomerSelfServiceMutationCommand): string {
  return command.operation === "CREATE" ? "提交预约" : `取消预约 ${command.reservationId}`;
}

function waitText(reservation: CustomerSelfServiceReservationReadModel): string | null {
  switch (reservation.waitEstimateKind) {
    case "READY_NOW":
      return "门店当前有空闲球台，可以尽快为你安排；最终以工作人员确认为准。";
    case "CURRENT_IDLE_TABLES":
      return `当前估计还需约 ${reservation.estimatedWaitMinutes} 分钟。只按现有空闲球台和预约时长估算，不保证实际时间。`;
    case "UNAVAILABLE_NO_IDLE_TABLE":
      return "当前没有空闲球台，在用台结束时间未知，因此暂时不能给出可靠等待分钟数。";
    case "NOT_IN_QUEUE":
      return null;
  }
}

function recoveryViewIsBlocking<T>(view: CustomerSelfServiceRecoveryView<T>): boolean {
  return view.status !== "NONE";
}

export function CustomerSelfServicePortal(): React.JSX.Element {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const legacyQueryKeys = useMemo(() => [...query.keys()], [query]);
  const legacyTenantIds = useMemo(() => query.getAll("tenantId"), [query]);
  const legacyStoreIds = useMemo(() => query.getAll("storeId"), [query]);
  const visitHeadingId = useId();
  const bookingHeadingId = useId();
  const ownBookingsHeadingId = useId();
  const groupBuyHeadingId = useId();
  const ownGroupBuyHeadingId = useId();
  const [initializing, setInitializing] = useState(true);
  const [bootstrap, setBootstrap] = useState<CustomerSelfServiceBootstrapReadModel | null>(null);
  const [entryError, setEntryError] = useState<{ title: string; text: string } | null>(null);
  const [sessionState, setSessionState] = useState<{
    session: CustomerVisitSessionReadModel;
    csrfToken: string;
  } | null>(null);
  const [workspace, setWorkspace] = useState<CustomerSelfServiceWorkspace | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [partySize, setPartySize] = useState("2");
  const [requestedStart, setRequestedStart] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("60");
  const [note, setNote] = useState("");
  const [groupBuyProvider, setGroupBuyProvider] = useState<"MEITUAN" | "DOUYIN">("MEITUAN");
  const [groupBuyVoucherSuffix, setGroupBuyVoucherSuffix] = useState("");
  const [groupBuyNote, setGroupBuyNote] = useState("");
  const [cancelReasonById, setCancelReasonById] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [recoveryRevision, setRecoveryRevision] = useState(0);
  const tenantId = bootstrap?.tenantId ?? "";
  const storeId = bootstrap?.storeId ?? "";

  const identity = useMemo<CustomerSelfServiceIdentity | null>(
    () =>
      sessionState
        ? {
            tenantId: sessionState.session.tenantId,
            storeId: sessionState.session.storeId,
            visitId: sessionState.session.visitId,
          }
        : null,
    [sessionState],
  );

  const creationRecovery = bootstrap
    ? readCustomerVisitSessionCreationRecovery({ tenantId, storeId })
    : ({ status: "NONE" } as const);
  const createRecovery = identity
    ? readCustomerSelfServiceMutationRecovery(identity, "CREATE")
    : ({ status: "NONE" } as const);
  const cancelRecovery = identity
    ? readCustomerSelfServiceMutationRecovery(identity, "CANCEL")
    : ({ status: "NONE" } as const);
  const revokeRecovery = identity
    ? readCustomerVisitSessionRevokeRecovery(identity)
    : ({ status: "NONE" } as const);
  const groupBuyRecovery = identity
    ? readCustomerGroupBuyRequestRecovery(identity)
    : ({ status: "NONE" } as const);
  void recoveryRevision;
  const mutationBlocked =
    busy ||
    recoveryViewIsBlocking(createRecovery) ||
    recoveryViewIsBlocking(cancelRecovery) ||
    recoveryViewIsBlocking(groupBuyRecovery) ||
    recoveryViewIsBlocking(revokeRecovery);

  const loadWorkspace = useCallback(
    async (
      expected: { session: CustomerVisitSessionReadModel; csrfToken: string },
      signal?: AbortSignal,
    ): Promise<void> => {
      setWorkspaceLoading(true);
      setWorkspaceError(null);
      try {
        const next = await getCustomerSelfServiceWorkspace(signal);
        if (
          next.session.tenantId !== expected.session.tenantId ||
          next.session.storeId !== expected.session.storeId ||
          next.session.visitId !== expected.session.visitId
        ) {
          setWorkspace(null);
          setWorkspaceError(
            "门店返回的访客会话与本设备保存的不一致。页面已停止显示顾客服务，请退出后重新扫码进入。",
          );
          return;
        }
        setSessionState({ session: next.session, csrfToken: expected.csrfToken });
        setWorkspace(next);
        setRequestedStart(
          (current) => current || defaultStartValue(next.generatedAt, next.timeZone),
        );
      } catch (error) {
        if (signal?.aborted) return;
        if (error instanceof CustomerSelfServiceApiError && error.status === 401) {
          const expectedIdentity = {
            tenantId: expected.session.tenantId,
            storeId: expected.session.storeId,
            visitId: expected.session.visitId,
          };
          setWorkspace(null);
          if (clearCustomerSelfServiceSession(expectedIdentity)) {
            setSessionState(null);
            setFeedback({
              kind: "INFO",
              text: "本设备的访客会话已失效，请重新登记后再使用顾客服务。",
            });
          } else {
            setWorkspaceError(
              "门店会话已失效，但本设备仍有待核对或已隔离的原请求。为避免丢失证据，当前不能重新登记、提交或退出，请让门店工作人员协助核对。",
            );
            setFeedback({
              kind: "ERROR",
              text: "会话已失效，但原请求仍待核对。本设备继续保留并锁定原请求，不能重新登记。",
            });
          }
          setRecoveryRevision((value) => value + 1);
          return;
        }
        setWorkspaceError(
          errorText(error, "暂时无法读取你的顾客服务进度。请保留本页面，稍后再试。"),
        );
      } finally {
        if (!signal?.aborted) setWorkspaceLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const abortController = new AbortController();
    let active = true;
    const initialize = async (): Promise<void> => {
      setInitializing(true);
      setEntryError(null);
      try {
        const authoritative = await getCustomerSelfServiceBootstrap(abortController.signal);
        if (!active) return;
        const currentUrl = new URL(window.location.href);
        const authoritativeEntryUrl = new URL(authoritative.customerEntryUrl);
        if (
          (currentUrl.protocol !== "https:" && currentUrl.protocol !== "http:") ||
          currentUrl.origin !== authoritativeEntryUrl.origin ||
          currentUrl.pathname !== authoritativeEntryUrl.pathname ||
          currentUrl.hash !== ""
        ) {
          setEntryError({
            title: "顾客自助入口不一致",
            text: "当前页面地址不属于门店公布的顾客入口。系统没有读取本设备资料，也不会提交访客登记；请使用门店当前提供的二维码或入口。",
          });
          return;
        }
        const hasNoLegacyScope = legacyQueryKeys.length === 0;
        const hasOneCompleteLegacyScope =
          legacyQueryKeys.length === 2 &&
          legacyQueryKeys.every((key) => key === "tenantId" || key === "storeId") &&
          legacyTenantIds.length === 1 &&
          legacyStoreIds.length === 1;
        const legacyTenantId = legacyTenantIds[0] ?? "";
        const legacyStoreId = legacyStoreIds[0] ?? "";
        if (
          !hasNoLegacyScope &&
          (!hasOneCompleteLegacyScope ||
            !stableIdPattern.test(legacyTenantId) ||
            !stableIdPattern.test(legacyStoreId) ||
            legacyTenantId !== authoritative.tenantId ||
            legacyStoreId !== authoritative.storeId)
        ) {
          setEntryError({
            title: "顾客自助链接无效",
            text: "链接中的门店信息不完整、格式错误或与当前门店不一致。系统没有读取本设备资料；请使用门店当前提供的入口，不要手工修改网址。",
          });
          return;
        }

        const stored = readCustomerSelfServiceSession();
        if (stored.status === "RECOVERY_STORAGE_UNAVAILABLE") {
          setEntryError({
            title: "当前浏览器无法安全进入",
            text: "浏览器无法安全读取本设备会话。为避免预约归属混乱，系统不会登记或提交预约；请换用正常浏览器后重新进入。",
          });
          return;
        }
        if (stored.status === "OWNED") {
          if (
            stored.sessionState.session.tenantId !== authoritative.tenantId ||
            stored.sessionState.session.storeId !== authoritative.storeId
          ) {
            setEntryError({
              title: "本设备已有其他门店会话",
              text: "本设备保存的访客会话不属于当前门店。系统没有读取该会话的预约，也不会向当前门店重新登记；请先回到原门店入口退出。",
            });
            return;
          }
          setBootstrap(authoritative);
          setSessionState(stored.sessionState);
          await loadWorkspace(stored.sessionState, abortController.signal);
          return;
        }
        setBootstrap(authoritative);
      } catch {
        if (!abortController.signal.aborted) {
          setBootstrap(null);
          setEntryError({
            title: "暂时无法进入顾客自助",
            text: "当前无法安全确认你进入的是哪家门店。系统没有读取本设备资料，也不会提交访客登记，请稍后重试或联系门店工作人员。",
          });
        }
      } finally {
        if (active) setInitializing(false);
      }
    };
    void initialize();
    return () => {
      active = false;
      abortController.abort();
    };
  }, [legacyQueryKeys, legacyStoreIds, legacyTenantIds, loadWorkspace]);

  const refreshRecoveries = (): void => setRecoveryRevision((value) => value + 1);

  const finishCreation = async (
    recovery: CustomerVisitSessionCreationRecovery,
    session: CustomerVisitSessionReadModel,
  ): Promise<void> => {
    if (!completeCustomerVisitSessionCreation(recovery, session)) {
      setFeedback({
        kind: "ERROR",
        text: "门店已创建访客会话，但本设备无法安全保存。请勿继续提交预约，关闭页面后向工作人员求助。",
      });
      return;
    }
    const nextSessionState = { session, csrfToken: recovery.request.csrfToken };
    setSessionState(nextSessionState);
    refreshRecoveries();
    setFeedback({ kind: "SUCCESS", text: "本设备访客登记已完成，现在可以使用已开放的顾客服务。" });
    await loadWorkspace(nextSessionState);
  };

  const dispatchSessionCreation = async (
    recovery: CustomerVisitSessionCreationRecovery,
  ): Promise<void> => {
    if (!beginCustomerVisitSessionCreationDispatch(recovery)) {
      setFeedback({
        kind: "ERROR",
        text: "原访客登记正在处理或需要先核对结果，本次没有重复发送。",
      });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const session = await createCustomerVisitSession(recovery.request);
      finishCustomerVisitSessionCreationDispatch(recovery);
      await finishCreation(recovery, session);
    } catch (error) {
      finishCustomerVisitSessionCreationDispatch(recovery);
      if (customerSelfServiceMutationOutcomeIsUnknown(error)) {
        markCustomerVisitSessionCreationOutcomeUnknown(recovery);
        refreshRecoveries();
        setFeedback({
          kind: "INFO",
          text: "访客登记结果暂时未知。页面已锁定原姓名、手机号和请求编号，请先核对，不能换资料重复提交。",
        });
      } else {
        clearCustomerVisitSessionCreationRecovery(recovery);
        refreshRecoveries();
        setFeedback({ kind: "ERROR", text: errorText(error, "访客登记未成功，请修改后再试。") });
      }
    } finally {
      setBusy(false);
    }
  };

  const createVisit = (): void => {
    const normalizedName = customerName.trim();
    const normalizedMobile = customerMobile.trim();
    if (!normalizedName || [...normalizedName].length > 60) {
      setFeedback({ kind: "ERROR", text: "请填写姓名，最多 60 个字。" });
      return;
    }
    if (!/^1[3-9][0-9]{9}$/u.test(normalizedMobile)) {
      setFeedback({ kind: "ERROR", text: "请填写 11 位中国大陆手机号，例如 13800138000。" });
      return;
    }
    let request: CreateCustomerVisitSessionRequest;
    try {
      request = {
        contractVersion: "1.0.0",
        tenantId,
        storeId,
        customerName: normalizedName,
        customerMobile: normalizedMobile,
        setupSecret: secureToken(),
        csrfToken: secureToken(),
        idempotencyKey: nextIdempotencyKey("customer-visit:create"),
      };
    } catch {
      setFeedback({
        kind: "ERROR",
        text: "当前浏览器无法生成安全的本设备会话，系统没有发送资料。请换用正常浏览器后重新扫码。",
      });
      return;
    }
    const claimed = claimCustomerVisitSessionCreationRecovery(request);
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        text: "当前浏览器无法安全锁定本次访客登记，系统没有发送资料。请保留页面并先处理已有请求。",
      });
      return;
    }
    void dispatchSessionCreation(claimed.recovery);
  };

  const dispatchMutation = async (recovery: CustomerSelfServiceMutationRecovery): Promise<void> => {
    if (!identity || !sessionState) return;
    if (!beginCustomerSelfServiceMutationDispatch(recovery, identity)) {
      setFeedback({
        kind: "ERROR",
        text: "原请求正在处理或需要先核对结果，本次没有重复发送。",
      });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const command = recovery.command;
      const result =
        command.operation === "CREATE"
          ? await createSelfServiceReservation(command.request, sessionState.csrfToken)
          : await cancelSelfServiceReservation(
              command.reservationId,
              command.request,
              sessionState.csrfToken,
            );
      finishCustomerSelfServiceMutationDispatch(recovery, identity);
      if (!customerSelfServiceMutationResultMatchesRecovery(recovery, result)) {
        quarantineCustomerSelfServiceMutationRecovery(recovery, identity);
        refreshRecoveries();
        setFeedback({
          kind: "ERROR",
          text: "门店回执与本设备保存的原请求不一致。该请求已永久锁定在本设备，不能再查询、清除或重试，请向门店工作人员核对。",
        });
        return;
      }
      if (!completeCustomerSelfServiceMutationRecovery(recovery, identity, result)) {
        setFeedback({
          kind: "ERROR",
          text: "门店已返回成功，但本设备无法清除保护记录。请勿再次操作并向工作人员求助。",
        });
        return;
      }
      refreshRecoveries();
      setFeedback({
        kind: "SUCCESS",
        text:
          result.operation === "CREATE"
            ? `预约已提交，编号 ${result.reservation.reservationNumber}。请等待门店确认，本次订金 0 元、优惠 0 元。`
            : `预约 ${result.reservation.reservationNumber} 已取消。`,
      });
      if (result.operation === "CREATE") setNote("");
      await loadWorkspace(sessionState);
    } catch (error) {
      finishCustomerSelfServiceMutationDispatch(recovery, identity);
      if (customerSelfServiceMutationOutcomeIsUnknown(error)) {
        markCustomerSelfServiceMutationOutcomeUnknown(recovery, identity);
        setFeedback({
          kind: "INFO",
          text: `${mutationLabel(recovery.command)}的结果暂时未知。页面已锁定原内容和请求编号，请先核对，不能改内容重复提交。`,
        });
      } else {
        clearCustomerSelfServiceMutationRecovery(recovery, identity);
        setFeedback({
          kind: "ERROR",
          text: errorText(error, "门店已明确拒绝本次操作，请刷新预约状态后再处理。"),
        });
        void loadWorkspace(sessionState);
      }
      refreshRecoveries();
    } finally {
      setBusy(false);
    }
  };

  const claimAndDispatch = (command: CustomerSelfServiceMutationCommand): void => {
    const claimed = claimCustomerSelfServiceMutationRecovery(command);
    refreshRecoveries();
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        text: "当前浏览器无法安全保存原请求，系统没有发送操作。请先处理页面上的待核对请求。",
      });
      return;
    }
    void dispatchMutation(claimed.recovery);
  };

  const createReservation = (): void => {
    if (!identity || !workspace) return;
    const size = Number(partySize);
    const duration = Number(durationMinutes);
    const normalizedNote = note.trim();
    if (!Number.isInteger(size) || size < 1 || size > 20) {
      setFeedback({ kind: "ERROR", text: "到店人数必须是 1 到 20 人的整数。" });
      return;
    }
    if (!Number.isInteger(duration) || duration < 30 || duration > 720 || duration % 30 !== 0) {
      setFeedback({
        kind: "ERROR",
        text: "预计时长必须按 30 分钟选择，范围为 30 分钟到 12 小时。",
      });
      return;
    }
    const converted = storeLocalDateTimeToIso(requestedStart, workspace.timeZone);
    if (converted.status !== "VALID") {
      setFeedback({
        kind: "ERROR",
        text:
          converted.status === "AMBIGUOUS"
            ? "所选时间在门店当地会出现两次，无法确定是哪一次。请改选前后其他时间。"
            : "所选到店时间在门店当地不存在或格式无效，请重新选择。",
      });
      return;
    }
    if ([...normalizedNote].length > 300) {
      setFeedback({ kind: "ERROR", text: "备注最多 300 个字，请删减后再提交。" });
      return;
    }
    let request: SelfServiceCustomerReservationCreateRequest;
    try {
      request = {
        contractVersion: "1.0.0",
        partySize: size,
        requestedStartAt: converted.value,
        durationMinutes: duration,
        note: normalizedNote || null,
        depositFen: 0,
        discountFen: 0,
        idempotencyKey: nextIdempotencyKey("customer-booking:create"),
      };
    } catch {
      setFeedback({ kind: "ERROR", text: "当前浏览器无法生成安全请求，系统没有提交预约。" });
      return;
    }
    claimAndDispatch({ operation: "CREATE", identity, request });
  };

  const cancelReservation = (reservation: CustomerSelfServiceReservationReadModel): void => {
    if (!identity) return;
    const reason = (cancelReasonById[reservation.reservationId] ?? "").trim();
    if ([...reason].length < 2 || [...reason].length > 200) {
      setFeedback({ kind: "ERROR", text: "请填写 2 到 200 个字的取消原因，方便门店核对。" });
      return;
    }
    let idempotencyKey: string;
    try {
      idempotencyKey = nextIdempotencyKey("customer-booking:cancel");
    } catch {
      setFeedback({ kind: "ERROR", text: "当前浏览器无法生成安全请求，系统没有取消预约。" });
      return;
    }
    claimAndDispatch({
      operation: "CANCEL",
      identity,
      reservationId: reservation.reservationId,
      request: {
        contractVersion: "1.0.0",
        expectedReservationVersion: reservation.reservationVersion,
        reason,
        idempotencyKey,
      },
    });
  };

  const reconcileMutation = async (
    recovery: CustomerSelfServiceMutationRecovery,
    retryIfMissing: boolean,
  ): Promise<void> => {
    if (!identity || !sessionState || busy) return;
    if (!customerSelfServiceMutationRecoveryCanQuery(recovery, identity)) {
      setFeedback({
        kind: "ERROR",
        text: "这条原请求已经因回执不一致而隔离，不能再次查询或重试，请向门店工作人员核对。",
      });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const result = await getCustomerSelfServiceMutationByRequest(
        recovery.command.request.idempotencyKey,
      );
      if (result !== null) {
        if (!customerSelfServiceMutationResultMatchesRecovery(recovery, result)) {
          quarantineCustomerSelfServiceMutationRecovery(recovery, identity);
          refreshRecoveries();
          setFeedback({
            kind: "ERROR",
            text: "查询结果与本设备保存的原请求不一致。该请求已永久隔离，不能再查询、清除或重试，请向门店工作人员核对。",
          });
          return;
        }
        if (!completeCustomerSelfServiceMutationRecovery(recovery, identity, result)) {
          setFeedback({
            kind: "ERROR",
            text: "已经查到原请求成功，但本设备无法清除保护记录。请勿再次操作。",
          });
          return;
        }
        refreshRecoveries();
        setFeedback({
          kind: "SUCCESS",
          text:
            result.operation === "CREATE"
              ? `已查到原预约，编号 ${result.reservation.reservationNumber}，不会重复提交。`
              : `已查到预约 ${result.reservation.reservationNumber} 的取消结果，不会重复取消。`,
        });
        await loadWorkspace(sessionState);
        return;
      }
      if (!retryIfMissing) {
        setFeedback({
          kind: "INFO",
          text: "门店确认没有执行原请求。你可以使用下方按钮按原内容和原请求编号安全重试。",
        });
        return;
      }
      if (!customerSelfServiceMutationCanRetryAfterNotFound(recovery, identity, result)) {
        setFeedback({
          kind: "ERROR",
          text: "当前原请求不满足安全重试条件，系统没有清除或重发。请向门店工作人员核对。",
        });
        return;
      }
      if (!clearCustomerSelfServiceMutationAfterNotFound(recovery, identity, result)) {
        setFeedback({
          kind: "ERROR",
          text: "门店确认没有执行，但本设备无法安全重置保护记录，因此没有重试。",
        });
        return;
      }
      const claimed = claimCustomerSelfServiceMutationRecovery(recovery.command);
      refreshRecoveries();
      if (claimed.status !== "OWNED") {
        setFeedback({ kind: "ERROR", text: "无法重新锁定原请求，因此没有重试。" });
        return;
      }
      setBusy(false);
      await dispatchMutation(claimed.recovery);
    } catch {
      setFeedback({
        kind: "INFO",
        text: "仍无法确认原请求结果。页面继续锁定原内容，请恢复连接后再核对。",
      });
    } finally {
      setBusy(false);
    }
  };

  const dispatchGroupBuyRequest = async (
    recovery: CustomerGroupBuyRequestRecovery,
  ): Promise<void> => {
    if (!identity || !sessionState) return;
    if (!beginCustomerGroupBuyRequestDispatch(recovery, identity)) {
      setFeedback({
        kind: "ERROR",
        text: "原团购请求正在处理或需要先核对结果，本次没有重复发送。",
      });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const result = await createSelfServiceGroupBuyRequest(
        recovery.request,
        sessionState.csrfToken,
      );
      finishCustomerGroupBuyRequestDispatch(recovery, identity);
      if (!customerGroupBuyRequestResultMatchesRecovery(recovery, result)) {
        quarantineCustomerGroupBuyRequestRecovery(recovery, identity);
        refreshRecoveries();
        setFeedback({
          kind: "ERROR",
          text: "门店回执与本设备保存的原团购请求不一致。原请求已永久隔离，不能再查询、清除或重试，请让工作人员核对。",
        });
        return;
      }
      if (!completeCustomerGroupBuyRequestRecovery(recovery, identity, result)) {
        setFeedback({
          kind: "ERROR",
          text: "门店已收到团购请求，但本设备无法清除保护记录。请勿再次提交并让工作人员核对。",
        });
        return;
      }
      refreshRecoveries();
      setGroupBuyVoucherSuffix("");
      setGroupBuyNote("");
      setFeedback({
        kind: "SUCCESS",
        text: `团购到店请求 ${result.request.requestNumber} 已提交。门店尚未核销券码或开台，请向收银员出示平台中的完整券码。`,
      });
      await loadWorkspace(sessionState);
    } catch (error) {
      finishCustomerGroupBuyRequestDispatch(recovery, identity);
      if (customerSelfServiceMutationOutcomeIsUnknown(error)) {
        markCustomerGroupBuyRequestOutcomeUnknown(recovery, identity);
        setFeedback({
          kind: "INFO",
          text: "团购请求结果暂时未知。页面已锁定原平台、券码尾号和请求编号，请先查询原请求，不能换内容重复提交。",
        });
      } else {
        clearCustomerGroupBuyRequestRecovery(recovery, identity);
        setFeedback({
          kind: "ERROR",
          text: errorText(error, "门店已明确拒绝本次团购请求，请按提示修改后再试。"),
        });
        void loadWorkspace(sessionState);
      }
      refreshRecoveries();
    } finally {
      setBusy(false);
    }
  };

  const createGroupBuyRequest = (): void => {
    if (!identity || !workspace?.groupBuyRequestEnabled) return;
    const suffix = groupBuyVoucherSuffix.normalize("NFKC").replace(/\s+/gu, "").toUpperCase();
    const normalizedNote = groupBuyNote.trim();
    if (!/^[A-Z0-9]{2,4}$/u.test(suffix)) {
      setFeedback({
        kind: "ERROR",
        text: "请填写完整券码最后 2 到 4 位，只能使用数字或英文字母。",
      });
      return;
    }
    if (normalizedNote.length === 1 || [...normalizedNote].length > 200) {
      setFeedback({ kind: "ERROR", text: "到店说明可不填；填写时请输入 2 到 200 个字。" });
      return;
    }
    let request: SelfServiceGroupBuyRequestCreateRequest;
    try {
      request = {
        contractVersion: "1.0.0",
        provider: groupBuyProvider,
        voucherCodeSuffix: suffix,
        note: normalizedNote || null,
        idempotencyKey: nextIdempotencyKey("customer-group-buy:create"),
      };
    } catch {
      setFeedback({ kind: "ERROR", text: "当前浏览器无法生成安全请求，系统没有提交团购请求。" });
      return;
    }
    const claimed = claimCustomerGroupBuyRequestRecovery(identity, request);
    refreshRecoveries();
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        text: "当前浏览器无法安全锁定本次团购请求，系统没有发送。请先处理页面上的待核对请求。",
      });
      return;
    }
    void dispatchGroupBuyRequest(claimed.recovery);
  };

  const reconcileGroupBuyRequest = async (
    recovery: CustomerGroupBuyRequestRecovery,
    retryIfMissing: boolean,
  ): Promise<void> => {
    if (!identity || !sessionState || busy) return;
    if (!customerGroupBuyRequestRecoveryCanQuery(recovery, identity)) {
      setFeedback({
        kind: "ERROR",
        text: "这条原团购请求已经隔离或不满足安全查询条件，不能再次查询或重试，请让工作人员核对。",
      });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const result = await getCustomerGroupBuyRequestByRequest(recovery.request.idempotencyKey);
      if (result !== null) {
        if (!customerGroupBuyRequestResultMatchesRecovery(recovery, result)) {
          quarantineCustomerGroupBuyRequestRecovery(recovery, identity);
          refreshRecoveries();
          setFeedback({
            kind: "ERROR",
            text: "查询结果与本设备保存的原团购请求不一致。原请求已永久隔离，不能再查询、清除或重试。",
          });
          return;
        }
        if (!completeCustomerGroupBuyRequestRecovery(recovery, identity, result)) {
          setFeedback({
            kind: "ERROR",
            text: "已经查到原团购请求，但本设备无法清除保护记录。请勿再次提交。",
          });
          return;
        }
        refreshRecoveries();
        setFeedback({
          kind: "SUCCESS",
          text: `已查到原团购请求 ${result.request.requestNumber}：${groupBuyRequestStateLabels[result.request.status]}，不会重复提交。`,
        });
        await loadWorkspace(sessionState);
        return;
      }
      if (!retryIfMissing) {
        setFeedback({
          kind: "INFO",
          text: "门店确认没有收到原团购请求。你可以使用下方按钮按原内容和原请求编号安全重试。",
        });
        return;
      }
      if (!clearCustomerGroupBuyRequestAfterNotFound(recovery, identity, result)) {
        setFeedback({
          kind: "ERROR",
          text: "门店确认没有收到原请求，但本设备无法安全重置保护记录，因此没有重试。",
        });
        return;
      }
      const claimed = claimCustomerGroupBuyRequestRecovery(identity, recovery.request);
      refreshRecoveries();
      if (claimed.status !== "OWNED") {
        setFeedback({ kind: "ERROR", text: "无法重新锁定原团购请求，因此没有重试。" });
        return;
      }
      setBusy(false);
      await dispatchGroupBuyRequest(claimed.recovery);
    } catch {
      setFeedback({
        kind: "INFO",
        text: "仍无法确认原团购请求结果。页面继续锁定原内容，请恢复连接后再核对。",
      });
    } finally {
      setBusy(false);
    }
  };

  const closeResolvedVisit = (text: string): void => {
    setSessionState(null);
    setWorkspace(null);
    setWorkspaceError(null);
    refreshRecoveries();
    setFeedback({ kind: "SUCCESS", text });
  };

  const dispatchRevoke = async (recovery: CustomerVisitSessionRevokeRecovery): Promise<void> => {
    if (!identity || !sessionState || busy) return;
    if (!beginCustomerVisitSessionRevokeDispatch(recovery, identity)) {
      setFeedback({
        kind: "ERROR",
        text: "原退出请求正在处理、需要先核对或已经隔离，本次没有重复发送。",
      });
      return;
    }
    setBusy(true);
    setFeedback(null);
    try {
      const result = await revokeCustomerVisitSession(recovery.request, sessionState.csrfToken);
      finishCustomerVisitSessionRevokeDispatch(recovery, identity);
      if (!customerVisitSessionRevokeResultMatchesRecovery(recovery, result)) {
        quarantineCustomerVisitSessionRevokeRecovery(recovery, identity);
        refreshRecoveries();
        setFeedback({
          kind: "ERROR",
          text: "退出回执与本设备原会话不一致。原退出请求已永久隔离，不能再查询、清除或重试，请向工作人员核对。",
        });
        return;
      }
      if (!completeCustomerVisitSessionRevoke(recovery, identity, result)) {
        setFeedback({
          kind: "ERROR",
          text: "门店已退出会话，但本设备无法安全清除会话与保护记录。请勿再次操作并向工作人员求助。",
        });
        return;
      }
      closeResolvedVisit("已退出本设备。再次预约需要重新登记。");
    } catch (error) {
      finishCustomerVisitSessionRevokeDispatch(recovery, identity);
      if (customerVisitSessionIsExactlyInvalid(error)) {
        if (clearCustomerVisitSessionRevokeAfterConfirmedInvalid(recovery, identity, error)) {
          closeResolvedVisit("门店确认原会话已退出或过期，本设备资料已清除。");
        } else {
          setFeedback({
            kind: "ERROR",
            text: "门店确认原会话已失效，但本设备无法安全清除保护记录。请勿再次操作。",
          });
        }
      } else if (customerSelfServiceMutationOutcomeIsUnknown(error)) {
        markCustomerVisitSessionRevokeOutcomeUnknown(recovery, identity);
        refreshRecoveries();
        setFeedback({
          kind: "INFO",
          text: "退出结果暂时未知。本设备已持久保留原退出请求，请先核对；刷新页面也不会生成新请求。",
        });
      } else if (clearCustomerVisitSessionRevokeAfterDefiniteNoEffect(recovery, identity, error)) {
        refreshRecoveries();
        setFeedback({
          kind: "ERROR",
          text: `${errorText(error, "门店明确拒绝了退出请求").replace(/[。；，、\s]+$/u, "")}。本设备会话仍保留，你可以核对原因后再次操作。`,
        });
      } else {
        quarantineCustomerVisitSessionRevokeRecovery(recovery, identity);
        refreshRecoveries();
        setFeedback({
          kind: "ERROR",
          text: `${errorText(error, "门店拒绝了退出请求")}。为保留证据，原退出请求已隔离，不能再次发送，请向工作人员核对。`,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const startRevoke = (): void => {
    if (!identity) return;
    let request: CustomerVisitSessionRevokeRequest;
    try {
      request = {
        contractVersion: "1.0.0",
        idempotencyKey: nextIdempotencyKey("customer-visit:revoke"),
      };
    } catch {
      setFeedback({ kind: "ERROR", text: "当前浏览器无法生成安全退出请求，本次没有退出。" });
      return;
    }
    const claimed = claimCustomerVisitSessionRevokeRecovery(identity, request);
    refreshRecoveries();
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        text: "当前浏览器无法安全保存原退出请求，因此没有发送。请先处理页面上的待核对请求。",
      });
      return;
    }
    void dispatchRevoke(claimed.recovery);
  };

  const reconcileRevoke = async (
    recovery: CustomerVisitSessionRevokeRecovery,
    retryIfStillActive: boolean,
  ): Promise<void> => {
    if (!identity || !sessionState || busy || recovery.state === "QUARANTINED") return;
    setBusy(true);
    setFeedback(null);
    try {
      const next = await getCustomerSelfServiceWorkspace();
      if (
        next.session.visitId !== identity.visitId ||
        next.session.tenantId !== identity.tenantId ||
        next.session.storeId !== identity.storeId
      ) {
        quarantineCustomerVisitSessionRevokeRecovery(recovery, identity);
        refreshRecoveries();
        setFeedback({
          kind: "ERROR",
          text: "当前门店会话与原退出请求不一致。原退出请求已永久隔离，不能重试，请向工作人员核对。",
        });
        return;
      }
      setWorkspace(next);
      setSessionState({ session: next.session, csrfToken: sessionState.csrfToken });
      if (!retryIfStillActive) {
        setFeedback({
          kind: "INFO",
          text: "门店确认原会话仍有效。你可以按原请求安全重试退出；不会生成新的请求编号。",
        });
        return;
      }
      setBusy(false);
      await dispatchRevoke(recovery);
    } catch (error) {
      if (
        customerVisitSessionIsExactlyInvalid(error) &&
        clearCustomerVisitSessionRevokeAfterConfirmedInvalid(recovery, identity, error)
      ) {
        closeResolvedVisit("已确认门店会话退出或过期，本设备资料已清除。");
        return;
      }
      setFeedback({ kind: "INFO", text: "仍无法确认退出结果，请保留页面并稍后再核对。" });
    } finally {
      setBusy(false);
    }
  };

  if (initializing) {
    return (
      <main className="customer-portal">
        <output className="customer-portal-loading">正在安全确认当前门店与本设备访客会话…</output>
      </main>
    );
  }

  if (entryError || !bootstrap) {
    return (
      <main className="customer-portal">
        <div className="customer-portal-shell">
          <section className="customer-portal-card" role="alert">
            <h1>{entryError?.title ?? "暂时无法进入顾客自助"}</h1>
            <p className="customer-portal-help">
              {entryError?.text ??
                "当前无法安全确认门店入口。系统没有读取或提交本设备资料，请稍后重试。"}
            </p>
          </section>
        </div>
      </main>
    );
  }

  const creationBlocked = creationRecovery.status !== "NONE";

  return (
    <main className="customer-portal">
      <div className="customer-portal-shell">
        <header className="customer-portal-header">
          <p className="customer-portal-eyebrow">BILLIARD OS · 顾客自助</p>
          <h1>
            {bootstrap.reservationEnabled && bootstrap.groupBuyRequestEnabled
              ? "预约到店或提交团购请求"
              : bootstrap.groupBuyRequestEnabled
                ? "提交团购到店请求，查看处理进度"
                : "预约到店，查看自己的进度"}
          </h1>
          <p>服务门店：{bootstrap.storeName}</p>
          <p>这里只显示本设备访客会话下的服务记录，不会显示其他顾客或门店内部经营资料。</p>
        </header>

        {feedback ? (
          <div
            className="customer-portal-feedback"
            data-kind={feedback.kind}
            role={feedback.kind === "ERROR" ? "alert" : "status"}
          >
            {feedback.text}
          </div>
        ) : null}

        {!sessionState ? (
          <section className="customer-portal-card" aria-labelledby={visitHeadingId}>
            <div className="customer-portal-section-heading">
              <h2 id={visitHeadingId}>先登记本设备访客</h2>
              <p>姓名用于门店核对和叫号；手机号仅用于这次访客服务。</p>
            </div>
            <div className="customer-portal-notice">
              <strong>请先了解：</strong>
              此处手机号未经短信验证，不会自动合并或创建会员。会话只保存在当前设备；换设备、清理浏览器数据或会话过期后无法找回，请向门店重新登记。
            </div>
            {creationRecovery.status === "OWNED" ? (
              <section className="customer-portal-recovery" aria-live="polite">
                <div>
                  <strong>访客登记结果待核对</strong>
                  原姓名、手机号、安全凭证和请求编号已锁定。只能原样重放同一登记请求，不能读取旧会话猜测结果，也不能换资料重复提交。
                </div>
                <div className="customer-portal-actions">
                  <button
                    className="customer-portal-primary"
                    type="button"
                    disabled={busy}
                    onClick={() => void dispatchSessionCreation(creationRecovery.recovery)}
                  >
                    按原资料和原请求号安全重试
                  </button>
                </div>
              </section>
            ) : creationRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
              <div className="customer-portal-feedback" data-kind="ERROR" role="alert">
                浏览器无法读取原访客登记。为避免重复创建，当前不能继续，请保留页面并向工作人员求助。
              </div>
            ) : creationRecovery.status === "OTHER_IDENTITY" ? (
              <div className="customer-portal-feedback" data-kind="ERROR" role="alert">
                本设备还保存着另一家门店的访客登记原请求。为避免把资料提交到错误门店，当前不能继续，请回到原链接核对。
              </div>
            ) : null}
            <form
              className="customer-portal-form"
              onSubmit={(event) => {
                event.preventDefault();
                createVisit();
              }}
            >
              <fieldset className="customer-portal-fieldset" disabled={busy || creationBlocked}>
                <label>
                  姓名
                  <input
                    autoComplete="name"
                    maxLength={60}
                    value={customerName}
                    onChange={(event) => setCustomerName(event.target.value)}
                  />
                  <span className="customer-portal-field-help">用于门店现场核对和叫号。</span>
                </label>
                <label>
                  本次联系手机号
                  <input
                    autoComplete="tel"
                    inputMode="tel"
                    maxLength={11}
                    value={customerMobile}
                    onChange={(event) => setCustomerMobile(event.target.value)}
                  />
                  <span className="customer-portal-field-help">
                    仅用于本次访客联系，未经验证，不会自动变成会员手机号。
                  </span>
                </label>
                <button className="customer-portal-primary" type="submit">
                  在本设备开始访客会话
                </button>
              </fieldset>
            </form>
          </section>
        ) : (
          <>
            <section className="customer-portal-card customer-portal-session-bar">
              <div className="customer-portal-section-heading">
                <h2>本设备访客会话</h2>
                <p>退出后，本设备不能再查看这些预约或团购请求。</p>
              </div>
              <dl className="customer-portal-session-meta">
                <div>
                  <dt>访客姓名</dt>
                  <dd>{sessionState.session.customerName}</dd>
                </div>
                <div>
                  <dt>联系手机号</dt>
                  <dd>{sessionState.session.customerMobileMasked}</dd>
                </div>
                <div>
                  <dt>会话有效期至</dt>
                  <dd>
                    {displayDateTime(
                      sessionState.session.expiresAt,
                      workspace?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                    )}
                  </dd>
                </div>
              </dl>
              {revokeRecovery.status === "OWNED" ? (
                <section className="customer-portal-recovery" aria-live="polite">
                  {revokeRecovery.recovery.state === "QUARANTINED" ? (
                    <div>
                      <strong>原退出请求已隔离</strong>
                      门店曾返回与本设备会话不一致的结果。为保留证据，这条请求不能再查询、清除或重试，请向工作人员核对。
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>退出结果待核对</strong>
                        本设备已持久保留原退出请求。先核对门店会话，不能生成新的退出请求。
                      </div>
                      <div className="customer-portal-actions">
                        <button
                          className="customer-portal-secondary"
                          type="button"
                          disabled={busy}
                          onClick={() => void reconcileRevoke(revokeRecovery.recovery, false)}
                        >
                          核对退出结果
                        </button>
                        <button
                          className="customer-portal-danger"
                          type="button"
                          disabled={busy}
                          onClick={() => void reconcileRevoke(revokeRecovery.recovery, true)}
                        >
                          核对后使用原退出请求安全重试
                        </button>
                      </div>
                    </>
                  )}
                </section>
              ) : revokeRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
                <div className="customer-portal-feedback" data-kind="ERROR" role="alert">
                  浏览器无法读取原退出请求。为避免重复退出，当前所有操作已锁定，请向门店工作人员求助。
                </div>
              ) : revokeRecovery.status === "OTHER_IDENTITY" ? (
                <div className="customer-portal-feedback" data-kind="ERROR" role="alert">
                  本设备保存着另一位访客的退出请求。当前不会显示或重试其内容，请向门店工作人员求助。
                </div>
              ) : (
                <button
                  className="customer-portal-danger"
                  type="button"
                  disabled={busy || mutationBlocked}
                  onClick={startRevoke}
                >
                  退出本设备
                </button>
              )}
            </section>

            {workspaceError ? (
              <div className="customer-portal-feedback" data-kind="ERROR" role="alert">
                {workspaceError}
              </div>
            ) : null}

            {workspace?.reservationEnabled ? (
              <section className="customer-portal-card" aria-labelledby={bookingHeadingId}>
                <div className="customer-portal-section-heading">
                  <h2 id={bookingHeadingId}>提交预约</h2>
                  <p>选择门店当地到店时间和预计打球时长，提交后仍需门店工作人员确认。</p>
                </div>
                <div className="customer-portal-money-boundary">
                  <strong>本次订金 0 元，优惠 0 元</strong>
                  <span>提交预约不会扣款、不会占用会员余额，也不会自动占用球台。</span>
                </div>
                <form
                  className="customer-portal-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    createReservation();
                  }}
                >
                  <fieldset
                    className="customer-portal-fieldset"
                    disabled={mutationBlocked || !workspace}
                  >
                    <label>
                      到店人数
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={20}
                        value={partySize}
                        onChange={(event) => setPartySize(event.target.value)}
                      />
                      <span className="customer-portal-field-help">
                        填写实际到店人数，范围 1 到 20 人。
                      </span>
                    </label>
                    <label>
                      门店当地到店时间
                      <input
                        type="datetime-local"
                        step={1800}
                        value={requestedStart}
                        onChange={(event) => setRequestedStart(event.target.value)}
                      />
                      <span className="customer-portal-field-help">
                        按门店所在地时间选择；不存在或重复出现而无法确定的时间不会提交。
                      </span>
                    </label>
                    <label>
                      预计打球时长
                      <select
                        value={durationMinutes}
                        onChange={(event) => setDurationMinutes(event.target.value)}
                      >
                        {durationChoices.map((minutes) => (
                          <option value={minutes} key={minutes}>
                            {durationLabel(minutes)}
                          </option>
                        ))}
                      </select>
                      <span className="customer-portal-field-help">
                        仅用于门店安排和等待估算，不保证球台在该时长后一定释放。
                      </span>
                    </label>
                    <label>
                      给门店的备注（选填）
                      <textarea
                        maxLength={300}
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                      />
                      <span className="customer-portal-field-help">
                        最多 300 个字，不要填写支付密码等敏感信息。
                      </span>
                    </label>
                    <button
                      className="customer-portal-primary"
                      type="submit"
                      disabled={mutationBlocked || !workspace}
                    >
                      提交待门店确认的预约
                    </button>
                  </fieldset>
                </form>
              </section>
            ) : null}

            {workspace?.groupBuyRequestEnabled ? (
              <section className="customer-portal-card" aria-labelledby={groupBuyHeadingId}>
                <div className="customer-portal-section-heading">
                  <h2 id={groupBuyHeadingId}>通知门店办理团购券</h2>
                  <p>这里只向门店发送到店请求；最终核销和开台必须由收银员现场确认。</p>
                </div>
                <div className="customer-portal-money-boundary">
                  <strong>提交请求不会核销券码，也不会自动开台或扣款</strong>
                  <span>
                    为保护券码，本页只收集最后 2 到 4
                    位。到店后请向收银员展示平台里的完整券码，由工作人员重新扫码核验。
                  </span>
                </div>
                <form
                  className="customer-portal-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    createGroupBuyRequest();
                  }}
                >
                  <fieldset
                    className="customer-portal-fieldset"
                    disabled={mutationBlocked || !workspace}
                  >
                    <label>
                      购买平台
                      <select
                        value={groupBuyProvider}
                        onChange={(event) =>
                          setGroupBuyProvider(event.target.value as "MEITUAN" | "DOUYIN")
                        }
                      >
                        <option value="MEITUAN">美团（到店后向收银员展示美团完整券码）</option>
                        <option value="DOUYIN">抖音（到店后向收银员展示抖音完整券码）</option>
                      </select>
                      <span className="customer-portal-field-help">
                        当前选择：{groupBuyProviderLabels[groupBuyProvider]}
                        。门店会按这个平台查找可用套餐。
                      </span>
                    </label>
                    <label>
                      完整券码最后 2 到 4 位
                      <input
                        autoComplete="off"
                        inputMode="text"
                        minLength={2}
                        maxLength={4}
                        value={groupBuyVoucherSuffix}
                        placeholder="例如 83A6"
                        onChange={(event) => setGroupBuyVoucherSuffix(event.target.value)}
                      />
                      <span className="customer-portal-field-help">
                        仅用于收银员现场找到这条请求；不要在这里填写完整券码、支付密码或验证码。
                      </span>
                    </label>
                    <label>
                      到店说明（选填）
                      <textarea
                        maxLength={200}
                        value={groupBuyNote}
                        placeholder="例如 两人已到店，希望使用无烟区球台"
                        onChange={(event) => setGroupBuyNote(event.target.value)}
                      />
                      <span className="customer-portal-field-help">
                        可不填；填写时请输入 2 到 200
                        个字。说明不会替代收银员对券码、套餐和球台的核对。
                      </span>
                    </label>
                    <button
                      className="customer-portal-primary"
                      type="submit"
                      disabled={mutationBlocked || !workspace}
                    >
                      提交待收银员办理的团购请求
                    </button>
                  </fieldset>
                </form>
              </section>
            ) : null}

            {groupBuyRecovery.status === "OWNED" ? (
              <section className="customer-portal-recovery" aria-live="polite">
                {groupBuyRecovery.recovery.state === "QUARANTINED" ? (
                  <div>
                    <strong>原团购请求已隔离</strong>
                    门店曾返回与原平台、券码尾号或说明不一致的结果。为保留证据，这条请求不能再查询、清除或重试，请让工作人员核对。
                  </div>
                ) : (
                  <>
                    <div>
                      <strong>团购请求结果待核对</strong>
                      原平台、券码尾号、说明和请求编号已锁定。必须先查询门店记录，不能换内容或新建第二条请求。
                    </div>
                    <div className="customer-portal-actions">
                      <button
                        className="customer-portal-secondary"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void reconcileGroupBuyRequest(groupBuyRecovery.recovery, false)
                        }
                      >
                        核对原团购请求结果
                      </button>
                      <button
                        className="customer-portal-primary"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void reconcileGroupBuyRequest(groupBuyRecovery.recovery, true)
                        }
                      >
                        核对后使用原请求安全重试
                      </button>
                    </div>
                  </>
                )}
              </section>
            ) : groupBuyRecovery.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
              <section className="customer-portal-feedback" data-kind="ERROR" role="alert">
                浏览器无法读取待核对的团购请求。为避免重复提交，当前顾客服务入口已锁定，请让门店工作人员协助处理。
              </section>
            ) : groupBuyRecovery.status === "OTHER_IDENTITY" ? (
              <section className="customer-portal-feedback" data-kind="ERROR" role="alert">
                本设备保存着另一位访客的待核对团购请求。当前不会显示或重试其内容，请先回到原访客会话处理。
              </section>
            ) : null}

            {workspace?.groupBuyRequestEnabled ? (
              <section className="customer-portal-card" aria-labelledby={ownGroupBuyHeadingId}>
                <div className="customer-portal-section-heading">
                  <h2 id={ownGroupBuyHeadingId}>我的团购请求</h2>
                  <p>这里显示门店是否已开始办理；完整券码仍须当面交给收银员核验。</p>
                  {workspace ? (
                    <p>上次更新：{displayDateTime(workspace.generatedAt, workspace.timeZone)}</p>
                  ) : null}
                  <button
                    className="customer-portal-secondary"
                    type="button"
                    disabled={busy || workspaceLoading}
                    onClick={() => void loadWorkspace(sessionState)}
                  >
                    刷新团购请求进度
                  </button>
                </div>
                {workspaceLoading ? (
                  <output className="customer-portal-empty">正在读取你的团购请求…</output>
                ) : workspace.groupBuyRequests.length === 0 ? (
                  <div className="customer-portal-empty">本设备访客会话还没有团购请求。</div>
                ) : (
                  <div className="customer-portal-reservations">
                    {workspace.groupBuyRequests.map((request) => (
                      <article className="customer-portal-reservation" key={request.requestId}>
                        <div className="customer-portal-reservation-title">
                          <h3>{request.requestNumber}</h3>
                          <span>{groupBuyRequestStateLabels[request.status]}</span>
                        </div>
                        <dl className="customer-portal-reservation-meta">
                          <div>
                            <dt>购买平台</dt>
                            <dd>{groupBuyProviderLabels[request.provider]}</dd>
                          </div>
                          <div>
                            <dt>券码尾号</dt>
                            <dd>{request.voucherCodeSuffix}</dd>
                          </div>
                          <div>
                            <dt>提交时间</dt>
                            <dd>{displayDateTime(request.createdAt, workspace.timeZone)}</dd>
                          </div>
                        </dl>
                        {request.note ? <p>我的说明：{request.note}</p> : null}
                        <p className="customer-portal-wait">{groupBuyRequestStatusText(request)}</p>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            {(
              [
                ["CREATE", createRecovery],
                ["CANCEL", cancelRecovery],
              ] as const
            ).map(([operation, view]) =>
              view.status === "OWNED" ? (
                <section className="customer-portal-recovery" key={operation} aria-live="polite">
                  {view.recovery.state === "QUARANTINED" ? (
                    <div>
                      <strong>{mutationLabel(view.recovery.command)}已隔离</strong>
                      门店曾返回与原请求不一致的结果。为避免操作到错误预约，本设备已永久关闭这条请求的查询、清除和重试，请向工作人员核对。
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>{mutationLabel(view.recovery.command)}结果待核对</strong>
                        原预约、内容和请求编号已锁定。先查询门店记录，不能改成另一条预约或换内容重试。
                      </div>
                      <div className="customer-portal-actions">
                        <button
                          className="customer-portal-secondary"
                          type="button"
                          disabled={busy}
                          onClick={() => void reconcileMutation(view.recovery, false)}
                        >
                          核对原请求结果
                        </button>
                        <button
                          className="customer-portal-primary"
                          type="button"
                          disabled={busy}
                          onClick={() => void reconcileMutation(view.recovery, true)}
                        >
                          核对后使用原请求安全重试
                        </button>
                      </div>
                    </>
                  )}
                </section>
              ) : view.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
                <section
                  className="customer-portal-feedback"
                  data-kind="ERROR"
                  role="alert"
                  key={operation}
                >
                  浏览器无法读取待核对的预约请求。为避免重复操作，当前预约和取消入口已锁定，请向门店工作人员求助。
                </section>
              ) : view.status === "OTHER_IDENTITY" ? (
                <section
                  className="customer-portal-feedback"
                  data-kind="ERROR"
                  role="alert"
                  key={operation}
                >
                  本设备保存着另一位访客的待核对请求。当前不会显示或重试其内容，请先退出并让门店工作人员协助处理。
                </section>
              ) : null,
            )}

            {workspace?.reservationEnabled ? (
              <section className="customer-portal-card" aria-labelledby={ownBookingsHeadingId}>
                <div className="customer-portal-section-heading">
                  <h2 id={ownBookingsHeadingId}>我的预约</h2>
                  <p>排队位置和等待时间会变化，请以门店工作人员的现场确认为准。</p>
                  {workspace ? (
                    <p>上次更新：{displayDateTime(workspace.generatedAt, workspace.timeZone)}</p>
                  ) : null}
                  <button
                    className="customer-portal-secondary"
                    type="button"
                    disabled={busy || workspaceLoading}
                    onClick={() => {
                      setFeedback(null);
                      void loadWorkspace(sessionState);
                    }}
                  >
                    刷新我的预约进度
                  </button>
                </div>
                {workspaceLoading ? (
                  <output className="customer-portal-empty">正在读取你的预约…</output>
                ) : workspaceError ? (
                  <button
                    className="customer-portal-secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setFeedback(null);
                      void loadWorkspace(sessionState);
                    }}
                  >
                    重新读取我的预约
                  </button>
                ) : workspace?.reservations.length === 0 ? (
                  <div className="customer-portal-empty">本设备访客会话还没有预约。</div>
                ) : (
                  <div className="customer-portal-reservations">
                    {workspace?.reservations.map((reservation) => {
                      const estimate = waitText(reservation);
                      return (
                        <article
                          className="customer-portal-reservation"
                          key={reservation.reservationId}
                        >
                          <div className="customer-portal-reservation-title">
                            <h3>{reservation.reservationNumber}</h3>
                            <span>{stateLabels[reservation.state]}</span>
                          </div>
                          <dl className="customer-portal-reservation-meta">
                            <div>
                              <dt>到店时间</dt>
                              <dd>
                                {displayDateTime(reservation.requestedStartAt, workspace.timeZone)}
                              </dd>
                            </div>
                            <div>
                              <dt>到店人数</dt>
                              <dd>{reservation.partySize} 人</dd>
                            </div>
                            <div>
                              <dt>预计时长</dt>
                              <dd>
                                {(Date.parse(reservation.requestedEndAt) -
                                  Date.parse(reservation.requestedStartAt)) /
                                  60_000}{" "}
                                分钟
                              </dd>
                            </div>
                            {reservation.queuePosition ? (
                              <div>
                                <dt>当前排队位置</dt>
                                <dd>第 {reservation.queuePosition} 位</dd>
                              </div>
                            ) : null}
                            {reservation.assignedTableNumber ? (
                              <div>
                                <dt>已安排球台</dt>
                                <dd>{reservation.assignedTableNumber} 号台</dd>
                              </div>
                            ) : null}
                          </dl>
                          {reservation.note ? <p>我的备注：{reservation.note}</p> : null}
                          {estimate ? <p className="customer-portal-wait">{estimate}</p> : null}
                          {reservation.state === "PENDING_CONFIRMATION" ? (
                            <p className="customer-portal-notice">
                              门店尚未确认，也没有为你占用球台。请等待工作人员核对后再按确认结果到店。
                            </p>
                          ) : null}
                          {reservation.cancellationAllowed ? (
                            <form
                              className="customer-portal-cancel-form"
                              onSubmit={(event) => {
                                event.preventDefault();
                                cancelReservation(reservation);
                              }}
                            >
                              <label>
                                取消原因
                                <input
                                  maxLength={200}
                                  value={cancelReasonById[reservation.reservationId] ?? ""}
                                  onChange={(event) =>
                                    setCancelReasonById((current) => ({
                                      ...current,
                                      [reservation.reservationId]: event.target.value,
                                    }))
                                  }
                                />
                                <span className="customer-portal-field-help">
                                  取消后本次预约立即结束，不能继续排队或入座；本次订金为 0
                                  元，如需再约必须重新提交。
                                </span>
                              </label>
                              <button
                                className="customer-portal-danger"
                                type="submit"
                                disabled={mutationBlocked}
                              >
                                确认取消这条预约
                              </button>
                            </form>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
